import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    PaperAirplaneIcon,
    SparklesIcon,
    XMarkIcon,
    LightBulbIcon,
    UserIcon,
    ComputerDesktopIcon,
    ExclamationTriangleIcon,
    CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { useToast } from './ToastProvider';
import { callAIApi } from '../utils/aiMock';
import { updateCredits } from '../slices/authSlice';
import { Link } from 'react-router-dom';
import AiPricing from './AiPricing';

const AiAssistant = ({ cvData = null, sector = null, isOpen = false, onClose = () => {} }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    
               const userCredits = useSelector((state) => state.auth.user?.credits || 0);
           const token = useSelector((state) => state.auth.token);
           const userId = useSelector((state) => state.auth.user?.id);
           const dispatch = useDispatch();
           const { success, error: toastError } = useToast();

    // Messages de bienvenue contextuels
    const getWelcomeMessage = useCallback(() => {
        if (cvData) {
            return `Bonjour ! Je vois que vous travaillez sur votre CV. Je peux vous aider à l'améliorer en analysant votre contenu et en vous donnant des conseils personnalisés. Que souhaitez-vous améliorer en priorité ?`;
        }
        return `Bonjour ! Je suis votre assistant IA pour l'amélioration de CV. Je peux vous aider à créer, optimiser et personnaliser votre CV selon votre secteur d'activité. Comment puis-je vous aider aujourd'hui ?`;
    }, [cvData]);

    // Suggestions initiales basées sur le contexte
    const getInitialSuggestions = useCallback(() => {
        const baseSuggestions = [
            "Comment structurer mon CV ?",
            "Quels mots-clés utiliser ?",
            "Comment mettre en valeur mes compétences ?"
        ];

        if (sector) {
            baseSuggestions.unshift(`Comment optimiser mon CV pour le secteur ${sector} ?`);
        }

        if (cvData?.experience?.length > 0) {
            baseSuggestions.unshift("Comment améliorer la description de mes expériences ?");
        }

        return baseSuggestions.slice(0, 4);
    }, [sector, cvData]);

    // Initialiser avec un message de bienvenue
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([
                {
                    id: 'welcome',
                    type: 'ai',
                    content: getWelcomeMessage(),
                    timestamp: new Date(),
                    suggestions: getInitialSuggestions()
                }
            ]);
        }
    }, [isOpen, messages.length, getWelcomeMessage, getInitialSuggestions]);

    // Auto-scroll vers le bas
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus sur l'input quand le chat s'ouvre
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const sendMessage = async (content) => {
        if (!content.trim() || isLoading) return;

                       // Vérifier les crédits avant d'envoyer le message (1 crédit nécessaire)
               if (userCredits < 1) {
                   toastError('Crédits insuffisants. L\'assistant IA nécessite 1 crédit par utilisation.');
                   return;
               }

               // Vérifier que le token et l'userId sont disponibles
               if (!token || !userId) {
                   toastError('Erreur d\'authentification. Veuillez vous reconnecter.');
                   return;
               }

        const userMessage = {
            id: Date.now().toString(),
            type: 'user',
            content: content.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);
        setIsTyping(true);

        try {
            // Utiliser le mock en développement ou l'API en production
            const data = await callAIApi(
                content.trim(),
                cvData,
                sector,
                messages.slice(-10), // Envoyer les 10 derniers messages
                token,
                userId
            );
            
            const aiMessage = {
                id: (Date.now() + 1).toString(),
                type: 'ai',
                content: data.response,
                timestamp: new Date(),
                suggestions: data.suggestions || []
            };

            setMessages(prev => [...prev, aiMessage]);
            
            // Déduire 1 crédit pour chaque utilisation de l'assistant IA
            const newCredits = userCredits - 1;
            dispatch(updateCredits(newCredits));
            
            success(`Réponse IA reçue ! (${newCredits} crédits restants)`);

        } catch (error) {
            console.error('Erreur AI Assistant:', error);
            
            if (error.message === 'INSUFFICIENT_CREDITS') {
                toastError('Crédits insuffisants. Rechargez votre compte pour continuer.');
                
                const errorMessage = {
                    id: (Date.now() + 1).toString(),
                    type: 'ai',
                    content: "Désolé, vous n'avez plus assez de crédits pour utiliser l'assistant IA. Rechargez votre compte pour continuer.",
                    timestamp: new Date(),
                    isError: true
                };
                
                setMessages(prev => [...prev, errorMessage]);
            } else {
                toastError('Erreur de communication avec l\'IA. Veuillez réessayer.');
                
                const errorMessage = {
                    id: (Date.now() + 1).toString(),
                    type: 'ai',
                    content: "Désolé, je rencontre des difficultés techniques. Pouvez-vous reformuler votre question ?",
                    timestamp: new Date(),
                    isError: true
                };
                
                setMessages(prev => [...prev, errorMessage]);
            }
        } finally {
            setIsLoading(false);
            setIsTyping(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(inputMessage);
    };

    const handleSuggestionClick = (suggestion) => {
        sendMessage(suggestion);
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-4">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
            
            {/* Chat Window */}
            <div className="relative w-full max-w-md h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                            <SparklesIcon className="w-6 h-6" />
                        </div>
                                                 <div>
                             <h3 className="font-semibold">Assistant IA CV</h3>
                             <p className="text-sm text-blue-100">
                                 {isTyping ? 'En train d\'écrire...' : 'En ligne'}
                             </p>
                             <p className={`text-xs ${userCredits < 2 ? 'text-yellow-200' : 'text-blue-200'}`}>
                                 {userCredits} crédit{userCredits > 1 ? 's' : ''} disponible{userCredits > 1 ? 's' : ''}
                                 {userCredits < 2 && userCredits >= 1 && (
                                     <span className="ml-1">⚠️</span>
                                 )}
                             </p>
                         </div>
                    </div>
                                         <div className="flex items-center gap-2">
                         <button
                             onClick={() => setShowPricing(true)}
                             className="p-2 hover:bg-white/20 rounded-full transition-colors"
                             title="Tarifs IA"
                         >
                             <CurrencyDollarIcon className="w-4 h-4" />
                         </button>
                         <button
                             onClick={onClose}
                             className="p-2 hover:bg-white/20 rounded-full transition-colors"
                         >
                             <XMarkIcon className="w-5 h-5" />
                         </button>
                     </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => (
                        <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                                <div className={`flex items-start gap-2 ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                    {/* Avatar */}
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        message.type === 'user' 
                                            ? 'bg-blue-600 text-white' 
                                            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                                    }`}>
                                        {message.type === 'user' ? (
                                            <UserIcon className="w-4 h-4" />
                                        ) : (
                                            <ComputerDesktopIcon className="w-4 h-4" />
                                        )}
                                    </div>

                                    {/* Message Content */}
                                    <div className={`rounded-2xl px-4 py-3 ${
                                        message.type === 'user'
                                            ? 'bg-blue-600 text-white'
                                            : message.isError
                                            ? 'bg-red-50 text-red-800 border border-red-200'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                            {message.content}
                                        </p>
                                        <p className={`text-xs mt-2 ${
                                            message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                                        }`}>
                                            {formatTimestamp(message.timestamp)}
                                        </p>
                                    </div>
                                </div>

                                {/* Suggestions */}
                                {message.type === 'ai' && message.suggestions && message.suggestions.length > 0 && (
                                    <div className="mt-3 ml-10">
                                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                            <LightBulbIcon className="w-3 h-3" />
                                            Suggestions :
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {message.suggestions.map((suggestion, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                    className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-100 transition-colors border border-blue-200"
                                                >
                                                    {suggestion}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <div className="flex justify-start">
                            <div className="max-w-[80%]">
                                <div className="flex items-start gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                                        <ComputerDesktopIcon className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="bg-gray-100 rounded-2xl px-4 py-3">
                                        <div className="flex space-x-1">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                                 {/* Input */}
                 <div className="p-4 border-t border-gray-200">
                     {userCredits < 1 && (
                         <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                             <div className="flex items-center gap-2 mb-2">
                                 <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                                 <div className="text-sm">
                                     <p className="text-red-700 font-medium">Crédits insuffisants</p>
                                     <p className="text-red-600">L'assistant IA nécessite 1 crédit par utilisation. Rechargez votre compte pour continuer.</p>
                                 </div>
                             </div>
                             <Link 
                                 to="/subscription" 
                                 className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs px-3 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                             >
                                 Recharger mes crédits
                             </Link>
                         </div>
                     )}
                     <form onSubmit={handleSubmit} className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            placeholder="Posez votre question..."
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={isLoading}
                        />
                                                 <button
                             type="submit"
                             disabled={!inputMessage.trim() || isLoading || userCredits < 1}
                             className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
                                 userCredits < 1 
                                     ? 'bg-gray-400 cursor-not-allowed' 
                                     : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                             }`}
                             title={userCredits < 1 ? 'Crédits insuffisants (1 crédit requis)' : 'Envoyer'}
                         >
                             <PaperAirplaneIcon className="w-4 h-4" />
                         </button>
                    </form>
                </div>
            </div>

            {/* Modal des tarifs */}
            <AiPricing 
                isVisible={showPricing}
                onClose={() => setShowPricing(false)}
            />
        </div>
    );
};

export default AiAssistant;
